import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Edit } from 'lucide-react'; // Import the Edit icon
import { isEmpty } from '../utils/BasicUtilities';
import { formatBdPst } from '../utils/DateUtilities';
import { useApiCaller } from '../utils/ApiUtilities';
import { getCommentGetUrl, getCommentPutUrl } from '../utils/UrlUtilities';
import CloseButton from './ui/CloseButton';
import { itemsPerPageOptions } from '../utils/Constants';

// --- Global Mock Data for API Caller ---
// This array simulates the backend data store. It's placed outside the hook
// to ensure all calls to useApiCaller share the same data instance,
// preventing data reset on re-renders and enabling persistence for mock updates.
// const mockCommentsData = [
//     { comment_id: 1, user_email: 'risla002@ucr.edu', user_name: 'Risul', comment: 'comment 1', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2025-06-18T07:37:17.000Z', updated_at: '2025-06-18T07:37:17.000Z', is_deleted: 0 },
//     { comment_id: 2, user_email: 'risla002@ucr.edu', user_name: 'Risul', comment: 'comment 2', resolution_status: 'resolved', resolution_status_updated_by_email: 'risla002@ucr.edu', resolution_comment: 'admin comment 2', created_at: '2025-06-18T07:37:46.000Z', updated_at: '2025-06-18T07:39:33.000Z', is_deleted: 0 },
//     { comment_id: 3, user_email: 'user2@example.com', user_name: 'Jane Doe', comment: 'I have a question about my account settings.', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2023-01-12T09:15:00Z', updated_at: '2023-01-12T09:15:00Z', is_deleted: 0 },
//     { comment_id: 4, user_email: 'moderator@example.com', user_name: 'Mod User', comment: 'Comment reported as spam. Needs review.', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2023-01-12T14:00:00Z', updated_at: '2023-01-12T14:00:00Z', is_deleted: 0 },
//     { comment_id: 5, user_email: 'user1@example.com', user_name: 'Alice Smith', comment: 'The new design looks great!', resolution_status: 'no_action_needed', resolution_status_updated_by_email: 'admin@example.com', resolution_comment: 'Positive feedback.', created_at: '2023-01-13T16:45:00Z', updated_at: '2023-01-13T17:00:00Z', is_deleted: 0 },
//     { comment_id: 6, user_email: 'user3@example.com', user_name: 'Bob Johnson', comment: 'Having trouble uploading files, keeps failing.', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2023-01-14T08:00:00Z', updated_at: '2023-01-14T08:00:00Z', is_deleted: 0 },
//     { comment_id: 7, user_email: 'admin@example.com', user_name: 'Super Admin', comment: 'Replied to file upload issue, awaiting user response.', resolution_status: 'not_resolved', resolution_status_updated_by_email: 'admin@example.com', resolution_comment: 'Asked for more details on file type and size.', created_at: '2023-01-14T09:30:00Z', updated_at: '2023-01-14T09:45:00Z', is_deleted: 0 },
//     { comment_id: 8, user_email: 'user1@example.com', user_name: 'Alice Smith', comment: 'Another comment from Alice.', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2025-06-18T08:00:00Z', updated_at: '2025-06-18T08:00:00Z', is_deleted: 0 },
//     { comment_id: 9, user_email: 'user2@example.com', user_name: 'Jane Doe', comment: 'Question about the new feature.', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2025-06-18T09:00:00Z', updated_at: '2025-06-18T09:00:00Z', is_deleted: 0 },
//     { comment_id: 10, user_email: 'user3@example.com', user_name: 'Bob Johnson', comment: 'Bug report: app crashes on startup.', resolution_status: 'pending', resolution_status_updated_by_email: null, resolution_comment: null, created_at: '2025-06-18T10:00:00Z', updated_at: '2025-06-18T10:00:00Z', is_deleted: 0 },
//     { comment_id: 11, user_email: 'user4@example.com', user_name: 'Charlie Brown', comment: 'Performance is slow today.', resolution_status: 'not_resolved', resolution_status_updated_by_email: 'admin_mock@example.com', resolution_comment: 'Investigating server logs.', created_at: '2025-06-18T11:00:00Z', updated_at: '2025-06-18T11:30:00Z', is_deleted: 0 },
//     { comment_id: 12, user_email: 'user5@example.com', user_name: 'Diana Prince', comment: 'Feedback on user interface.', resolution_status: 'no_action_needed', resolution_status_updated_by_email: 'admin_mock@example.com', resolution_comment: 'Forwarded to UX team.', created_at: '2025-06-18T12:00:00Z', updated_at: '2025-06-18T12:15:00Z', is_deleted: 0 },
// ];


// --- Mocked API Caller Hook ---
// This hook simulates API calls for fetching and updating comments.
// It now directly uses the module-scoped `mockCommentsData` array.
// const useApiCaller = () => {
//     const commentGetHandler = useCallback(async ({ page, limit, user_email, resolution_status, comment }) => {
//         // Ensure page and limit are numbers
//         const numPage = Number(page);
//         const numLimit = Number(limit);

//         // Simulate network delay
//         await new Promise(resolve => setTimeout(resolve, 500));

//         let filteredComments = [...mockCommentsData]; // Operate on a copy for filtering

//         // Apply filters
//         if (user_email) {
//             filteredComments = filteredComments.filter(c =>
//                 c.user_email.toLowerCase().includes(user_email.toLowerCase())
//             );
//         }
//         if (comment) {
//             filteredComments = filteredComments.filter(c =>
//                 c.comment.toLowerCase().includes(comment.toLowerCase())
//             );
//         }
//         if (resolution_status && resolution_status !== 'all') { // 'all' for the dropdown
//             filteredComments = filteredComments.filter(c =>
//                 c.resolution_status === resolution_status
//             );
//         }

//         // Sort by comment_id descending to match API response sample order
//         filteredComments.sort((a, b) => b.comment_id - a.comment_id);

//         const total = filteredComments.length;
//         const startIndex = (numPage - 1) * numLimit;
//         const endIndex = startIndex + numLimit;
//         const paginatedData = filteredComments.slice(startIndex, endIndex);

//         return { success: true, data: paginatedData, total: total };
//     }, []); // No dependencies for useCallback as it operates on the global mockCommentsData

//     const commentPutHandler = useCallback(async (id, payload) => {
//         // Simulate network delay
//         await new Promise(resolve => setTimeout(resolve, 500));

//         const commentIndex = mockCommentsData.findIndex(c => c.comment_id === id);

//         if (commentIndex === -1) {
//             return { success: false, message: 'Comment not found.' };
//         }

//         // Directly modify the global mockCommentsData array
//         mockCommentsData[commentIndex] = {
//             ...mockCommentsData[commentIndex],
//             ...payload,
//             updated_at: new Date().toISOString(), // Update timestamp
//             // Simulate who updated it (e.g., current logged-in user)
//             resolution_status_updated_by_email: 'admin_mock@example.com' // Replace with actual authenticated user email
//         };
//         return { success: true, data: mockCommentsData[commentIndex] };
//     }, []); // No dependencies for useCallback

//     // Return an object that mirrors the structure of a real API client.
//     return {
//         getComments: commentGetHandler,
//         updateComment: commentPutHandler
//     };
// };

// --- CommentFilterBar Component ---
// Manages the filter input fields and the apply/clear buttons.
function CommentFilterBar({ onApplyFilters, onClearFilters, initialFilters }) {
    const [email, setEmail] = useState(initialFilters.user_email || '');
    const [commentText, setCommentText] = useState(initialFilters.comment || '');
    const [status, setStatus] = useState(initialFilters.resolution_status || 'all'); // 'all' for dropdown default

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'pending', label: 'Pending' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'not_resolved', label: 'Not Resolved' },
        { value: 'no_action_needed', label: 'No Action Needed' }
    ];

    const handleApply = () => {
        onApplyFilters({
            user_email: email,
            comment: commentText,
            resolution_status: status,
        });
    };

    const handleClear = () => {
        setEmail('');
        setCommentText('');
        setStatus('all');
        onClearFilters(); // Notify parent to clear filters
    };

    return (
        <div className="card shadow-sm p-4 mb-4 rounded-lg kp-filter">
            <h2 className="card-title h5 mb-3 text-dark">Filter Comments</h2>
            {/* Using Bootstrap's row and col classes for alignment */}
            <div className="row g-3 align-items-end mb-4">
                <div className="col-md"> {/* Auto-width columns for inputs */}
                    <label htmlFor="filter-email" className="form-label text-muted">User Email</label>
                    <input
                        type="text"
                        id="filter-email"
                        className="form-control"
                        placeholder="e.g., user@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </div>
                <div className="col-md">
                    <label htmlFor="filter-comment" className="form-label text-muted">Comment Text</label>
                    <input
                        type="text"
                        id="filter-comment"
                        className="form-control"
                        placeholder="e.g., bug, question"
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                    />
                </div>
                <div className="col-md">
                    <label htmlFor="filter-status" className="form-label text-muted">Status</label>
                    <select
                        id="filter-status"
                        className="form-select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                {/* Buttons column - using ms-auto to push to the right */}
                <div className="col-md-auto d-flex justify-content-end gap-2 ms-auto mt-4 mt-md-0"> {/* ms-auto for right alignment, mt-md-0 for responsive margin */}
                    <button
                        onClick={handleApply}
                        className="btn btn-primary shadow-sm"
                    >
                        Apply Filters
                    </button>
                    <button
                        onClick={handleClear}
                        className="btn btn-outline-secondary shadow-sm"
                    >
                        Clear Filters
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- CommentEditModal Component ---
// Modal for editing comment resolution status and comment.
function CommentEditModal({ comment, onSave, onCancel}) {
    const [resolutionStatus, setResolutionStatus] = useState(comment.resolution_status);
    const [resolutionComment, setResolutionComment] = useState(comment.resolution_comment || '');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    const CallApi = useApiCaller();

    const statusOptions = [
        { value: 'pending', label: 'Pending' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'not_resolved', label: 'Not Resolved' },
        { value: 'no_action_needed', label: 'No Action Needed' }
    ];

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const payload = {
                resolution_status: resolutionStatus,
                resolution_comment: resolutionComment,
            };
            const commentPutUrl = getCommentPutUrl(comment.comment_id);
            const commentPutResponse = await CallApi({
                url: commentPutUrl,
                method: 'PUT',
                payload: payload,
                token: true,
            });
            // const response = await api.updateComment(comment.comment_id, payload);

            if (commentPutResponse.success) {
                onSave(commentPutResponse.data);
            } else {
                setError('Failed to update comment.');
            }
        } catch (err) {
            console.error('Error saving comment:', err);
            setError('An unexpected error occurred while saving.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        // Bootstrap Modal structure
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document"> {/* Added modal-lg for wider modal */}
                <div className="modal-content">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title">Edit Comment Status</h5>
                        <CloseButton onClick={onCancel} />
                    </div>
                    <div className="modal-body">
                        {error && (
                            <div className="alert alert-danger" role="alert">
                                {error}
                            </div>
                        )}
                        <div className="mb-3">
                            <label className="form-label">User Email:</label>
                            <p className="form-control-plaintext">{comment.user_email}</p>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">User Name:</label>
                            <p className="form-control-plaintext">{comment.user_name}</p>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Comment Text:</label>
                            <p className="form-control bg-light p-2 rounded-md border">{comment.comment}</p>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="resolution-status" className="form-label">Resolution Status</label>
                            <select
                                id="resolution-status"
                                className="form-select"
                                value={resolutionStatus}
                                onChange={(e) => setResolutionStatus(e.target.value)}
                                disabled={isSaving}
                            >
                                {statusOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="resolution-comment" className="form-label">Resolution Comment</label>
                            <textarea
                                id="resolution-comment"
                                rows="3"
                                className="form-control"
                                placeholder="Add internal notes about the resolution..."
                                value={resolutionComment}
                                onChange={(e) => setResolutionComment(e.target.value)}
                                disabled={isSaving}
                            ></textarea>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={onCancel}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}


// --- CommentTable Component ---
// Displays the comments in a table with pagination.
function CommentTable({ comments, totalComments, loading, error, currentPage, pageSize, onPageChange, onPageSizeChange, onEditComment }) {
    const totalPages = Math.ceil(totalComments / pageSize);

    // Function to get appropriate badge style for status
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending': return 'bg-warning text-dark';
            case 'resolved': return 'bg-success text-white';
            case 'not_resolved': return 'bg-danger text-white';
            case 'no_action_needed': return 'bg-info text-dark';
            case 'report_revoked': return 'bg-primary text-white'; // Bootstrap primary for purple
            default: return 'bg-secondary text-white';
        }
    };

    return (
        <div className="card shadow-sm p-4 rounded-lg">
            <h2 className="card-title h5 mb-3 text-dark">Comments</h2>
            {loading && (
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-2">Loading comments...</p>
                </div>
            )}
            {error && (
                <div className="alert alert-danger" role="alert">
                    <strong>Error!</strong> {error}
                </div>
            )}
            {!loading && !error && (
                <>
                    <div className="table-responsive rounded-lg border">
                        <table className="table table-hover table-striped mb-0">
                            <thead className="table-secondary">
                                <tr>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        Comment ID
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        User Email
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        User Name
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        Comment
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        Status
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        Resolution Updated By
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        Resolution Comment
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-start text-uppercase">
                                        Created At
                                    </th>
                                    <th scope="col" className="px-3 py-3 text-center text-uppercase">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {comments.length === 0 ? (
                                    <tr>
                                        <td colSpan="9" className="text-center text-muted py-4">
                                            No comments found.
                                        </td>
                                    </tr>
                                ) : (
                                    comments.map((comment) => (
                                        <tr key={comment.comment_id}>
                                            <td className="px-3 py-3">{comment.comment_id}</td>
                                            <td className="px-3 py-3">{comment.user_email}</td>
                                            <td className="px-3 py-3">{comment.user_name}</td>
                                            <td className="px-3 py-3 text-truncate" style={{maxWidth: '200px'}} title={comment.comment}>
                                                {comment.comment}
                                            </td>
                                            <td className="px-3 py-3">
                                                <span className={`badge rounded-pill ${getStatusBadgeClass(comment.resolution_status)}`}>
                                                    {comment.resolution_status.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3">{comment.resolution_status_updated_by_email || 'N/A'}</td>
                                            <td className="px-3 py-3 text-truncate" style={{maxWidth: '150px'}} title={comment.resolution_comment}>
                                                {comment.resolution_comment || 'N/A'}
                                            </td>
                                            <td className="px-3 py-3">{formatBdPst(comment.created_at)}</td>
                                            <td className="px-3 py-3 text-center">
                                                <button
                                                    onClick={() => onEditComment(comment)}
                                                    className="btn btn-link p-0 text-primary"
                                                    title="Edit Comment"
                                                >
                                                    <Edit size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center gap-2">
                            <label htmlFor="pageSizeSelect" className="form-label text-muted mb-0">Items per page:</label>
                            <select
                                id="pageSizeSelect"
                                value={pageSize}
                                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                className="form-select form-select-sm w-auto"
                            >
                                {itemsPerPageOptions.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                                {/* <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option> */}
                            </select>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <button
                                onClick={() => {
                                    const newPage = currentPage === 1 ? totalPages : currentPage - 1;
                                    onPageChange(newPage);
                                }}
                                className="btn btn-outline-secondary rounded-pill"
                            >
                                Previous
                            </button>
                            <span className="text-muted">Page {currentPage} of {totalPages === 0 ? 1 : totalPages} · Total: {totalComments}</span>
                            <button
                                onClick={() => {
                                    const newPage = currentPage === totalPages || totalPages === 0 ? 1 : currentPage + 1;
                                    onPageChange(newPage);
                                }}
                                className="btn btn-outline-secondary rounded-pill"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// --- Main CommentGridPage Component ---
// Orchestrates the filter bar and comment table.
function CommentGridPage() {
    const [appliedFilters, setAppliedFilters] = useState({
        user_email: '',
        comment: '',
        resolution_status: 'all',
    });

    const [comments, setComments] = useState([]);
    const [totalComments, setTotalComments] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showEditModal, setShowEditModal] = useState(false);
    const [editingComment, setEditingComment] = useState(null);

    // const api = useApiCaller();
    const CallApi = useApiCaller();

    const fetchComments = async () => {
        setLoading(true);
        setError(null);
        try {
            const queryParams = {
                page: currentPage,
                limit: pageSize
            };
            if (!isEmpty(appliedFilters.user_email)) {
                queryParams["user_email"] = appliedFilters.user_email;
            }

            if (!isEmpty(appliedFilters.comment)) {
                queryParams["comment"] = appliedFilters.comment;
            }

            if (!isEmpty(appliedFilters.resolution_status) && appliedFilters.resolution_status !== 'all') {
                queryParams["resolution_status"] = appliedFilters.resolution_status;
            }

            const commentGetUrl = getCommentGetUrl(queryParams);
            const commentGetResponse = await CallApi({
                url: commentGetUrl,
                method: 'GET',
                token: true,
            });

            // const response = await api.getComments(queryParams);

            if (commentGetResponse.success) {
                setComments(commentGetResponse.data?.comments);
                setTotalComments(commentGetResponse.data?.total);
            } else {
                setComments([]);
                setTotalComments(0);
                setError('Failed to fetch comments.');
            }
        } catch (err) {
            console.error('Error fetching comments:', err);
            setError('An unexpected error occurred while fetching comments.');
            setComments([]);
            setTotalComments(0);
        } finally {
            setLoading(false);
        }
    };

    // Fetch comments on filter or pagination change
    useEffect(() => {
        fetchComments();
    }, [appliedFilters, currentPage, pageSize]);

    const handleApplyFilters = (newFilters) => {
        setAppliedFilters(newFilters);
        setCurrentPage(1); // Reset to first page
    };

    const handleClearFilters = () => {
        setAppliedFilters({ user_email: '', comment: '', resolution_status: 'all' });
        setCurrentPage(1); // Reset to first page
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1); // Reset to first page when size changes
    };

    const handleEditComment = (comment) => {
        setEditingComment(comment);
        setShowEditModal(true);
    };

    const handleSaveEdit = (updatedComment) => {
        setShowEditModal(false);
        setEditingComment(null);
        // After save, refetch comments to reflect changes
        // This will auto-trigger due to dependency change from modal close
        setTimeout(() => {
            // To ensure latest comment is fetched (updated state)
            setCurrentPage((prev) => prev); // force re-fetch
            fetchComments();
        }, 0);
    };

    const handleCancelEdit = () => {
        setShowEditModal(false);
        setEditingComment(null);
    };

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <h1 className="mb-4 fw-bold" style={{ color: '#5b21b6', fontSize: '1.7rem' }}>
                Comment Management
            </h1>

            <div className="container-fluid">
                <CommentFilterBar
                    onApplyFilters={handleApplyFilters}
                    onClearFilters={handleClearFilters}
                    initialFilters={appliedFilters}
                />
                <CommentTable
                    comments={comments}
                    totalComments={totalComments}
                    loading={loading}
                    error={error}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    onEditComment={handleEditComment}
                />
            </div>

            {showEditModal && editingComment && (
                <CommentEditModal
                    comment={editingComment}
                    onSave={handleSaveEdit}
                    onCancel={handleCancelEdit}
                />
            )}
        </div>
    );
}


export default CommentGridPage;
